import type { ExceptionInfo, FlatMap, StackFrame } from "./types";

// ---------------------------------------------------------------------------
// THE scrubber. Every telemetry event passes through here before it is queued
// (server events directly, browser events again in /api/telemetry), so the
// privacy rules (ids only: no text, names, emails, tokens, URLs; see
// docs/telemetry.md) live in one place:
//
// - text (messages, log lines, span descriptions): email addresses, bearer /
//   API keys / JWTs, codici fiscali, VAT numbers, IBANs, phone numbers, IP
//   addresses, token-like query values and whole query strings are replaced
//   by [redacted]; a "quoted" value that isn't a plain SQL identifier (file
//   names, people's names in our [area] log lines) too; absolute host paths
//   become app:/// paths;
// - routes: never a query or a fragment, ids collapsed to [id];
// - free-form maps (tags, data, attributes): flat, at most 30 keys, and the
//   forbidden keys (email, name, token, url, body, detail, …) are dropped;
// - errors: name, scrubbed message, code and stack frames — never a pg
//   error's detail / where / parameters (they repeat the failing row).
//
// No Node imports: the browser reporter uses the same functions.
// ---------------------------------------------------------------------------

export const REDACTED = "[redacted]";

const MAX_MAP_KEYS = 30;
const MAP_KEY_RE = /^[a-z0-9_.]{1,40}$/;

/** Forbidden keys, compared lower-case with `_` and `-` removed. */
const FORBIDDEN_KEYS = new Set(
  [
    "email",
    "name",
    "first_name",
    "last_name",
    "username",
    "phone",
    "password",
    "token",
    "secret",
    "api_key",
    "authorization",
    "cookie",
    "set_cookie",
    "ip",
    "ip_address",
    "body",
    "request_body",
    "form",
    "query",
    "query_string",
    "url",
    "headers",
    "detail",
    "parameters",
    "values",
  ].map((k) => k.replace(/[_-]/g, "")),
);

export function isForbiddenKey(key: string): boolean {
  return FORBIDDEN_KEYS.has(key.toLowerCase().replace(/[_-]/g, ""));
}

// Order matters: tokens before emails before the number patterns.
const TEXT_RULES: [RegExp, string][] = [
  // Authorization values and our / Stripe / Conductor key shapes.
  [/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi, `$1 ${REDACTED}`],
  [/\b(?:cer|ctk|sk|pk|rk|whsec|ck)_[A-Za-z0-9_]{8,}/g, REDACTED],
  // JWT / JWE shaped.
  [/\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}(?:\.[A-Za-z0-9_-]*)*/g, REDACTED],
  // key=value pairs whose value is a credential (query strings, log lines).
  [
    /\b(token|invite|key|apikey|api_key|password|pass|secret|signature|sig|session|otp)=([^\s&"'#]+)/gi,
    `$1=${REDACTED}`,
  ],
  // Absolute URLs keep their origin only (push endpoints, signed download
  // links and Conductor file links carry credentials in the path).
  [/(https?:\/\/[^\s/?#"']+)[/?#][^\s"']*/g, `$1/${REDACTED}`],
  // Any remaining query string on a path.
  [/(\/[^\s?#"']*)\?[^\s"'#]+/g, `$1?${REDACTED}`],
  // Email addresses.
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, REDACTED],
  // IBAN (before the VAT rule: "IT60X0542811101000000123456").
  [/\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/g, REDACTED],
  // Italian codice fiscale (16 chars) and VAT numbers (IT + 11 digits, or bare 11).
  [/\b[A-Za-z]{6}\d{2}[A-Za-z]\d{2}[A-Za-z]\d{3}[A-Za-z]\b/g, REDACTED],
  [/\bIT\s?\d{11}\b/gi, REDACTED],
  [/(?<![\w-])\d{11}(?![\w-])/g, REDACTED],
  // Phone numbers (international form, or long digit groups).
  [/\+\d[\d\s().-]{7,}\d/g, REDACTED],
  // IPv4 addresses.
  [/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, REDACTED],
];

/** A quoted value is kept only when it's a plain identifier (a constraint, a relation). */
const QUOTED_RE = /"([^"\n]{0,200})"/g;
const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** Absolute host paths outside the repo: only their tail is kept. */
const HOST_PATH_RE = /(?:file:\/\/)?\/(?:home|root|srv|opt|var|tmp|Users|usr)\/[^\s:)'"]+/g;

let appRoot = "";

/** The repo root, for turning absolute file paths into app:/// paths (server only). */
export function setAppRoot(root: string): void {
  appRoot = root.replace(/\/+$/, "");
}

function relativePath(path: string): string {
  let p = path.replace(/^file:\/\//, "");
  if (appRoot && p.startsWith(`${appRoot}/`)) return `app:///${p.slice(appRoot.length + 1)}`;
  const nm = p.lastIndexOf("/node_modules/");
  if (nm >= 0) return `app:///${p.slice(nm + 1)}`;
  const next = p.indexOf("/.next/");
  if (next >= 0) return `app:///${p.slice(next + 1)}`;
  if (/^\/(?:home|root|srv|opt|var|tmp|Users|usr)\//.test(p)) {
    p = p.split("/").slice(-2).join("/");
    return `app:///${p}`;
  }
  return p;
}

/** Scrubs one piece of free text and cuts it to `max` characters. */
export function scrubText(input: unknown, max = 2000): string {
  let s = typeof input === "string" ? input : input == null ? "" : String(input);
  if (s.length > max * 4) s = s.slice(0, max * 4);
  s = s.replace(HOST_PATH_RE, (m) => relativePath(m));
  for (const [re, to] of TEXT_RULES) s = s.replace(re, to);
  s = s.replace(QUOTED_RE, (m, inner: string) => (IDENTIFIER_RE.test(inner) ? m : `"${REDACTED}"`));
  s = s.replace(/\s+/g, " ").trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A route for the `route` field: a pattern is kept as it is; a raw path loses
 * its query / fragment, and segments that look like ids or tokens become
 * [id] / [token]. At most 200 characters.
 */
export function scrubRoute(raw: unknown): string | undefined {
  if (typeof raw !== "string" || !raw) return undefined;
  let path = raw.split(/[?#]/, 1)[0];
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return undefined;
    }
  }
  if (path.startsWith("job:")) return path.slice(0, 200);
  if (!path.startsWith("/")) return undefined;
  const segments = path.split("/").map((seg) => {
    if (!seg || /^\[.*\]$/.test(seg) || /^\(.*\)$/.test(seg)) return seg;
    if (UUID_RE.test(seg)) return "[id]";
    if (/^\d+$/.test(seg)) return "[n]";
    if (seg.length >= 20 && /^[A-Za-z0-9_-]+$/.test(seg) && /\d/.test(seg)) return "[token]";
    if (seg.includes("@")) return "[redacted]";
    return seg.length > 60 ? "[segment]" : seg;
  });
  return segments.join("/").slice(0, 200) || "/";
}

/**
 * A flat map for tags / data / attributes: forbidden keys and non-flat values
 * dropped, strings scrubbed, at most 30 keys. `stringsOnly` for tags.
 */
export function scrubMap(input: unknown, opts: { stringsOnly?: boolean; maxValue?: number } = {}): FlatMap | undefined {
  if (!input || typeof input !== "object" || Array.isArray(input)) return undefined;
  const out: FlatMap = {};
  let n = 0;
  for (const [rawKey, value] of Object.entries(input as Record<string, unknown>)) {
    if (n >= MAX_MAP_KEYS) break;
    const key = rawKey.toLowerCase();
    if (!MAP_KEY_RE.test(key) || isForbiddenKey(key)) continue;
    if (typeof value === "string") {
      out[key] = scrubText(value, opts.maxValue ?? 200);
    } else if (typeof value === "number" && Number.isFinite(value)) {
      out[key] = opts.stringsOnly ? String(value) : value;
    } else if (typeof value === "boolean") {
      out[key] = opts.stringsOnly ? String(value) : value;
    } else {
      continue;
    }
    n++;
  }
  return n ? out : undefined;
}

export function scrubTags(input: unknown): Record<string, string> | undefined {
  return scrubMap(input, { stringsOnly: true }) as Record<string, string> | undefined;
}

// --- Errors ----------------------------------------------------------------

const V8_FRAME = /^\s*at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?\s*$/;
const GECKO_FRAME = /^\s*(.*?)@(.+?):(\d+):(\d+)\s*$/;

function frameFile(raw: string): string {
  let file = raw.trim();
  // Browser frames: keep the path of /_next/static/…, never the origin.
  const httpMatch = /^https?:\/\/[^/]+(\/[^?#]*)/.exec(file);
  if (httpMatch) return httpMatch[1];
  if (file.startsWith("webpack-internal:///")) file = file.slice("webpack-internal:///".length);
  if (file.startsWith("node:") || file === "native" || file === "<anonymous>") return file;
  return relativePath(file);
}

/** Up to 100 stack frames, innermost first (V8 and Firefox formats). */
export function parseStack(stack: unknown): StackFrame[] | undefined {
  if (typeof stack !== "string" || !stack) return undefined;
  const frames: StackFrame[] = [];
  for (const line of stack.split("\n").slice(0, 200)) {
    const m = V8_FRAME.exec(line) ?? GECKO_FRAME.exec(line);
    if (!m) continue;
    const file = frameFile(m[2]);
    const fn = m[1]?.trim();
    frames.push({
      ...(fn ? { function: scrubText(fn, 200) } : {}),
      file: scrubText(file, 300),
      line: Number(m[3]),
      column: Number(m[4]),
      in_app:
        !file.startsWith("node:") &&
        !file.includes("node_modules/") &&
        !file.includes("/next/dist/") &&
        file !== "native",
    });
    if (frames.length >= 100) break;
  }
  return frames.length ? frames : undefined;
}

type ErrorLike = Error & {
  code?: unknown;
  Code?: unknown;
  errno?: unknown;
  $metadata?: { httpStatusCode?: unknown };
};

function errorCode(e: ErrorLike): string | undefined {
  for (const c of [e.code, e.Code, e.errno, e.$metadata?.httpStatusCode]) {
    if (typeof c === "string" && c) return c.slice(0, 40);
    if (typeof c === "number") return String(c).slice(0, 40);
  }
  return undefined;
}

/**
 * The `exception` of an error event. Only name, message (scrubbed), code and
 * stack; a pg error's detail / where / parameters are never read.
 */
export function exceptionFrom(err: unknown, depth = 0): ExceptionInfo {
  if (err instanceof Error || (err && typeof err === "object" && "message" in err)) {
    const e = err as ErrorLike;
    const type = (e.name && e.name !== "Error" && e.name !== "error" ? e.name : e.constructor?.name) || "Error";
    const info: ExceptionInfo = {
      type: scrubText(type, 200) || "Error",
      message: scrubText(e.message, 2000),
    };
    const code = errorCode(e);
    if (code) info.code = scrubText(code, 40);
    const stack = parseStack(e.stack);
    if (stack) info.stack = stack;
    const cause = (e as { cause?: unknown }).cause;
    if (cause !== undefined && cause !== null && depth < 2) info.cause = exceptionFrom(cause, depth + 1);
    return info;
  }
  if (typeof err === "string") return { type: "Error", message: scrubText(err, 2000) };
  return { type: typeof err === "object" ? "Object" : typeof err, message: "non-error value thrown" };
}

/** Hex helpers for W3C trace ids, without node:crypto. */
export function randomHex(bytes: number): string {
  const buf = new Uint8Array(bytes);
  globalThis.crypto.getRandomValues(buf);
  let out = "";
  for (const b of buf) out += b.toString(16).padStart(2, "0");
  return out;
}

export function uuidV4(): string {
  return globalThis.crypto.randomUUID();
}

export function isUuid(v: unknown): v is string {
  return typeof v === "string" && UUID_RE.test(v);
}
